package com.bulut.mecitogullari.ui.screens.supplierdetail

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.i18n.Localization
import com.bulut.mecitogullari.core.util.YearMonth
import com.bulut.mecitogullari.core.util.newId
import com.bulut.mecitogullari.core.util.yearMonthOf
import com.bulut.mecitogullari.data.model.AccountKind
import com.bulut.mecitogullari.data.model.DebtEntry
import com.bulut.mecitogullari.data.model.DebtEntryType
import com.bulut.mecitogullari.data.model.Supplier
import com.bulut.mecitogullari.data.model.balance
import com.bulut.mecitogullari.data.repository.DebtRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** One month of the statement, newest month first. */
data class MonthGroup(
    val month: YearMonth,
    val purchases: Double,
    val payments: Double,
    val entries: List<DebtEntry>,
)

data class SupplierDetailUiState(
    val supplier: Supplier? = null,
    /** Positive = borç. */
    val balance: Double = 0.0,
    val totalPurchases: Double = 0.0,
    val totalPayments: Double = 0.0,
    val months: List<MonthGroup> = emptyList(),
)

class SupplierDetailViewModel(
    private val supplierId: String,
    private val debtRepository: DebtRepository,
) : ViewModel() {

    private val _supplierDeleted = MutableStateFlow(false)
    val supplierDeleted: StateFlow<Boolean> = _supplierDeleted.asStateFlow()

    val uiState: StateFlow<SupplierDetailUiState> = combine(
        debtRepository.suppliers,
        debtRepository.entriesFor(supplierId),
    ) { suppliers, entries ->
        val months = entries
            .groupBy { yearMonthOf(it.dateEpochMillis) }
            .map { (month, list) ->
                MonthGroup(
                    month = month,
                    purchases = list.filter { it.type == DebtEntryType.PURCHASE }.sumOf { it.amount },
                    payments = list.filter { it.type == DebtEntryType.PAYMENT }.sumOf { it.amount },
                    entries = list.sortedByDescending { it.dateEpochMillis },
                )
            }
            .sortedByDescending { it.month }
        SupplierDetailUiState(
            supplier = suppliers.firstOrNull { it.id == supplierId },
            balance = entries.balance(),
            totalPurchases = entries.filter { it.type == DebtEntryType.PURCHASE }.sumOf { it.amount },
            totalPayments = entries.filter { it.type == DebtEntryType.PAYMENT }.sumOf { it.amount },
            months = months,
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = SupplierDetailUiState(),
    )

    /** Manual movement: payment, or an item-less carried-over debt (devir). */
    fun addManualEntry(type: DebtEntryType, amount: Double, dateEpochMillis: Long, description: String) {
        if (amount <= 0) return
        viewModelScope.launch {
            debtRepository.upsertEntry(
                DebtEntry(
                    id = newId(),
                    supplierId = supplierId,
                    type = type,
                    dateEpochMillis = dateEpochMillis,
                    description = description.trim().ifBlank {
                        val isCustomer = uiState.value.supplier?.kind == AccountKind.CUSTOMER
                        if (type == DebtEntryType.PAYMENT) {
                            if (isCustomer) Localization.strings.pdfCollection else Localization.strings.pdfPayment
                        } else {
                            "Devreden bakiye"
                        }
                    },
                    amount = amount,
                )
            )
        }
    }

    fun deleteEntry(entryId: String) {
        viewModelScope.launch {
            debtRepository.deleteEntry(entryId)
        }
    }

    fun deleteSupplier() {
        viewModelScope.launch {
            debtRepository.deleteSupplier(supplierId)
            _supplierDeleted.value = true
        }
    }
}
